import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { DateRange, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment';
import { MinuteFrequencyOptionEnum } from '../../../shared/models';
import { DateKindEnum, DateTimeOfDay } from '../../../shared/models/daterange';

@Component({
  selector: 'dr-customer-offers-ui-date-time-range-picker',
  templateUrl: './date-time-range-picker.component.html',
  styleUrls: ['./date-time-range-picker.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, MatIconModule, MatFormFieldModule, MatDatepickerModule, TranslateModule]
})
export class DateTimeRangePickerComponent implements OnInit {
  //MARK: Inputs and Outputs
  @Input() minuteFrequency: MinuteFrequencyOptionEnum = 30;
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  @Input() initialStartDate: moment.Moment | null = null;
  @Input() initialEndDate: moment.Moment | null = null;

  // --- OUTPUTS ---
  @Output() apply = new EventEmitter<{ startDate: moment.Moment; endDate: moment.Moment }>();
  @Output() cancel = new EventEmitter<void>();

  //MARK: Signals and Computed Values
  readonly startHour12 = signal<number>(12);
  readonly startPeriod = signal<DateTimeOfDay>('am');
  readonly startMinute = signal<number>(0);

  readonly endHour12 = signal<number>(12);
  readonly endPeriod = signal<DateTimeOfDay>('am');
  readonly endMinute = signal<number>(0);
  readonly startDate = signal<Date | null>(null);
  readonly endDate = signal<Date | null>(null);

  readonly startTime = computed(() => {
    return `${this.startHour12()}:${this.startMinute().toString().padStart(2, '0')} ${this.startPeriod()}`;
  });

  readonly endTime = computed(() => {
    return `${this.endHour12()}:${this.endMinute().toString().padStart(2, '0')} ${this.endPeriod()}`;
  });

  readonly disableApply = computed(() => {
    const startDate = this.startDate();
    const endDate = this.endDate();
    if (!startDate || !endDate) {
      return true;
    }
    const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    if (startDay > endDay) {
      return true;
    }
    if (startDay.getTime() === endDay.getTime()) {
      const startHour = this.convertTo24Hour(this.startHour12(), this.startPeriod());
      const endHour = this.convertTo24Hour(this.endHour12(), this.endPeriod());
      const startMinute = this.startMinute();
      const endMinute = this.endMinute();
      if (startHour > endHour) {
        return true;
      } else if (startHour === endHour && endMinute <= startMinute) {
        return true;
      }
    }
    return false;
  });

  // This object is what tells mat-calendar to paint the visual range
  readonly selectedRange = computed<DateRange<Date>>(() => {
    const start = this.startDate();
    const end = this.endDate();
    return new DateRange(start, end);
  });

  protected dateKindEnum = DateKindEnum;

  //MARK: OnInit
  ngOnInit(): void {
    this.startDate.set(this.initialStartDate?.toDate() ?? null);
    this.endDate.set(this.initialEndDate?.toDate() ?? null);
  }

  //MARK: Methods
  protected incrementHour(type: DateKindEnum): void {
    // Increment the hours by 1 and update the converted time
    if (type === DateKindEnum.start) {
      this.startHour12() < 12 ? this.startHour12.update((value) => value + 1) : this.startHour12.set(1);
    } else if (type === DateKindEnum.end) {
      this.endHour12() < 12 ? this.endHour12.update((value) => value + 1) : this.endHour12.set(1);
    }
  }

  protected decrementHour(type: DateKindEnum): void {
    // Decrement the hours by 1 and update the converted time
    if (type === DateKindEnum.start) {
      this.startHour12() > 1 ? this.startHour12.update((value) => value - 1) : this.startHour12.set(12);
    } else if (type === DateKindEnum.end) {
      this.endHour12() > 1 ? this.endHour12.update((value) => value - 1) : this.endHour12.set(12);
    }
  }

  protected incrementMinuteByFrequency(type: DateKindEnum): void {
    const maxMinute = 59;
    let newMinute = 0;

    if (type === DateKindEnum.start) {
      newMinute = this.startMinute() + this.minuteFrequency;

      if (newMinute <= maxMinute) {
        this.startMinute.set(newMinute);
      } else {
        // Handle the case where the new minute exceeds 59
        this.startMinute.set(0);
      }
    } else if (type === DateKindEnum.end) {
      newMinute = this.endMinute() + this.minuteFrequency;

      if (newMinute <= maxMinute) {
        this.endMinute.set(newMinute);
      } else {
        // Handle the case where the new minute exceeds 59
        this.endMinute.set(0);
      }
    }
  }

  protected decrementMinuteByFrequency(type: DateKindEnum): void {
    const minMinute = 0;
    let newMinute = 0;

    if (type === DateKindEnum.start) {
      newMinute = this.startMinute() - this.minuteFrequency;
      if (newMinute >= minMinute) {
        this.startMinute.set(newMinute);
      } else {
        // Handle the case where the new minute goes below 0
        this.startMinute.set(60 - this.minuteFrequency);
      }
    } else if (type === DateKindEnum.end) {
      newMinute = this.endMinute() - this.minuteFrequency;
      if (newMinute >= minMinute) {
        this.endMinute.set(newMinute);
      } else {
        // Handle the case where the new minute goes below 0
        this.endMinute.set(60 - this.minuteFrequency);
      }
    }
  }

  protected onStartDateChange(date: Date | null): void {
    const startDate = this.startDate();

    if (startDate && startDate.getTime() === date?.getTime()) {
      this.startDate.set(null);
    } else {
      this.startDate.set(date);
    }

    // Safety check: If the user picks a new start date that is AFTER
    // the currently selected end date, reset the end date.
    const startDateUpdated = this.startDate();
    const endDateUpdated = this.endDate();
    if (startDateUpdated && endDateUpdated && startDateUpdated > endDateUpdated) {
      this.endDate.set(null);
    }
  }

  protected onEndDateChange(date: Date | null): void {
    const endDate = this.endDate();

    if (endDate && endDate.getTime() === date?.getTime()) {
      this.endDate.set(null);
    } else {
      this.endDate.set(date);
    }

    const startDate = this.startDate();
    const endDateUpdated = this.endDate();

    // Safety check: If the user picks a new end date that is BEFORE
    // the currently selected start date, reset the start date.
    if (startDate && endDateUpdated && endDateUpdated < startDate) {
      this.startDate.set(null);
    }
  }

  protected onApplyClick(): void {
    const startDate = this.startDate();
    const endDate = this.endDate();
    if (startDate && endDate) {
      this.apply.emit({
        startDate: moment({
          year: startDate.getFullYear(),
          month: startDate.getMonth(),
          day: startDate.getDate(),
          hour: this.convertTo24Hour(this.startHour12(), this.startPeriod()),
          minute: this.startMinute(),
          second: 0
        }),
        endDate: moment({
          year: endDate.getFullYear(),
          month: endDate.getMonth(),
          day: endDate.getDate(),
          hour: this.convertTo24Hour(this.endHour12(), this.endPeriod()),
          minute: this.endMinute(),
          second: 0
        })
      });
    }
  }

  private convertTo24Hour(hour12: number, period: DateTimeOfDay): number {
    if (period === 'am') {
      return hour12 === 12 ? 0 : hour12; // 12 AM is 0 in 24-hour format
    } else {
      return hour12 === 12 ? 12 : hour12 + 12; // 12 PM is 12 in 24-hour format
    }
  }
}
